import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  GroupIDandName,
  liveboardPermissions,
  MetadataPermission,
  PermissionArray,
  Principal,
} from "./types/thoughtSpot";
import { environmentConfig } from "../environment";

const tsV2ApiBaseURL = {
  baseUrl: `${environmentConfig.thoughtspotHost}/api/rest/2.0`,
};

export const thoughtspotV2Api = createApi({
  reducerPath: "thoughtspotV2Api",
  baseQuery: fetchBaseQuery(tsV2ApiBaseURL),
  endpoints: (builder) => ({
    getUserGroups: builder.mutation<
      GroupIDandName[],
      { userId: string; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        return {
          url: `/users/search`,
          method: "post",
          body: new URLSearchParams({
            user_identifier: `${arg.userId}`,
          }),
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
        };
      },
      transformResponse(response: any): GroupIDandName[] {
        return response.length !== 0 ? response[0].user_groups : [];
      },
    }),
    getUserDetails: builder.mutation<
      any,
      { userId: string; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        return {
          url: `/users/search`,
          method: "post",
          body: new URLSearchParams({
            user_identifier: `${arg.userId}`,
          }),
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
        };
      },
    }),
    addUserToGroup: builder.mutation<
      any,
      { userId: string; groupNames: string[]; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        var bodyParams = new URLSearchParams({
          operation: "ADD",
        });
        arg.groupNames.forEach((groupName) => {
          bodyParams.append("group_identifiers", groupName);
        });

        return {
          url: `/users/${arg.userId}/update`,
          method: "post",
          body: bodyParams,
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
        };
      },
    }),
    getLiveboardsAccessableByGroup: builder.mutation<
      PermissionArray[] | null,
      { IdArray: Principal[]; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        return {
          url: `/security/principals/fetch-permissions`,
          method: "post",
          body: {
            principals: arg.IdArray,
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
      transformResponse(response: any): PermissionArray[] | null {
        return response.principal_permission_details;
      },
    }),
    getWorksheetIDsInLiveboard: builder.mutation<
      string[],
      { liveboardId: string; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        return {
          url: `/metadata/search`,
          method: "post",
          body: {
            include_details: true,
            liveboard_response_version: "V2",
            metadata: [
              {
                type: "LIVEBOARD",
                identifier: arg.liveboardId,
              },
            ],
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
      transformResponse(response: any): string[] {
        var results: string[] = [];
        response[0].metadata_detail.reportContent.sheets.forEach(
          (element: any) => {
            if (element.sheetContent.hasOwnProperty("pinboardFilterDetails")) {
              results = results.concat(
                element.sheetContent.pinboardFilterDetails.filterLogicalTableIds.filter(
                  (item: any) => results.indexOf(item) < 0,
                ),
              );
            }
          },
        );

        return results;
      },
    }),
    getLiveboardArrayPermissions: builder.query<
      liveboardPermissions[],
      { liveboardArray: string[]; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        const metadataArray = arg.liveboardArray.map((liveboardID) => {
          return {
            identifier: liveboardID,
            type: "LIVEBOARD",
          };
        });
        return {
          url: `/security/metadata/fetch-permissions`,
          method: "post",
          body: {
            metadata: metadataArray,
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
      transformResponse(response: any): liveboardPermissions[] {
        const result: liveboardPermissions[] = [];
        response.metadata_permission_details.forEach((liveboardInfo: any) => {
          //liveboardInfo corresponds to the permissions of one liveboard
          const permissionArray: MetadataPermission[] = [];

          liveboardInfo.principal_permission_info.forEach(
            (permissionObject: any) => {
              //permissionObject corresponds to a specific group of permissions
              //if (permissionObject.principal_type !== "USER_GROUP") {
              permissionObject.principal_permissions.forEach(
                (actualPermissions: any) => {
                  //actualPermissions corresponds to actual user permissions
                  permissionArray.push({
                    metadata_id: actualPermissions.principal_id,
                    metadata_name: actualPermissions.principal_name,
                    permission: actualPermissions.permission,
                  });
                },
              );
              //}
            },
          );

          result.push({
            metadata_name: liveboardInfo.metadata_name,
            metadata_id: liveboardInfo.metadata_id,
            permissions: permissionArray,
          });
        });

        return result;
      },
    }),
    isLiveboardInOrg: builder.mutation<
      boolean,
      { liveboardId: string; thoughtSpotAccessToken: string }
    >({
      query: (arg) => {
        return {
          url: `/metadata/search`,
          method: "post",
          body: {
            include_details: false,
            include_headers: true,
            include_dependent_objects: false,
            include_hidden_objects: false,
            metadata: [
              {
                type: "LIVEBOARD",
                identifier: arg.liveboardId,
              },
            ],
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
      transformResponse(response: any): boolean {
        return response.length !== 0;
      },
    }),
    shareTSObject: builder.mutation<
      string[],
      {
        objectID: string | string[];
        typeOfObject: "LIVEBOARD" | "LOGICAL_TABLE";
        shareId: string;
        typeOfId: "USER_GROUP" | "USER";
        typeOfAccess: "READ_ONLY" | "MODIFY" | "NO_ACCESS";
        thoughtSpotAccessToken: string;
      }
    >({
      query: (arg) => {
        var identifiers =
          typeof arg.objectID === "string" ? [arg.objectID] : arg.objectID;
        return {
          url: `/security/metadata/share`,
          method: "post",
          body: {
            metadata_type: arg.typeOfObject,
            metadata_identifiers: identifiers,
            permissions: [
              {
                principal: {
                  identifier: arg.shareId,
                  type: arg.typeOfId,
                },
                share_mode: arg.typeOfAccess,
              },
            ],
            emails: [],
            message: "",
            notify_on_share: false,
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
    }),
    getMetadataDetails: builder.mutation<
      string[],
      {
        metadataIdArray: string[];
        metadataType: "LOGICAL_TABLE" | "LIVEBOARD";
        thoughtSpotAccessToken: string;
        responseVersion: "V1" | "V2";
      }
    >({
      query: (arg) => {
        const paramsArray = arg.metadataIdArray.map((metadataID) => {
          return {
            type: arg.metadataType,
            identifier: metadataID,
          };
        });
        return {
          url: `/metadata/search`,
          method: "post",
          body: {
            include_details: true,
            include_headers: true,
            include_dependent_objects: false,
            include_hidden_objects: false,
            metadata: paramsArray,
            liveboard_response_version: arg.responseVersion,
          },
          headers: {
            Authorization: `Bearer ${arg.thoughtSpotAccessToken}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
        };
      },
    }),
  }),
});

export const {
  useGetUserGroupsMutation,
  useGetLiveboardsAccessableByGroupMutation,
  useGetWorksheetIDsInLiveboardMutation,
  useShareTSObjectMutation,
  useIsLiveboardInOrgMutation,
  useGetUserDetailsMutation,
  useAddUserToGroupMutation,
  useLazyGetLiveboardArrayPermissionsQuery,
  useGetMetadataDetailsMutation,
} = thoughtspotV2Api;
